import { useCallback, useEffect, useRef, useState, type RefObject } from 'react';
import type { PixelRect } from '@chukua/vehicle-model/showcase';
import { showcaseMode } from '../policies/showcase-motion';
import { useRenderQuality } from './use-render-quality';

function prefersReducedMotion(): boolean {
  return typeof window.matchMedia === 'function' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

/** The layer's size and where the rig's frame sits inside it, in CSS pixels. */
export interface ShowcaseLayout {
  surface: { width: number; height: number };
  frame: PixelRect;
}

function measure(frame: Element, layer: Element): ShowcaseLayout {
  const inner = frame.getBoundingClientRect();
  const outer = layer.getBoundingClientRect();
  return {
    surface: { width: outer.width, height: outer.height },
    frame: { x: inner.left - outer.left, y: inner.top - outer.top, width: inner.width, height: inner.height },
  };
}

/**
 * The showcase layer's state: its size and where the rig's frame sits inside it, how it runs (`showcaseMode`),
 * and whether the 3D canvas is mounted yet, only once the layer is on screen, so a hidden panel
 * (the login page on a phone) never loads three.js.
 */
export function useRigShowcase(frameRef: RefObject<HTMLElement | null>) {
  const layerRef = useRef<HTMLDivElement>(null);
  const quality = useRenderQuality();
  const [mode] = useState(() => showcaseMode(quality, prefersReducedMotion()));
  const [layout, setLayout] = useState<ShowcaseLayout | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [is3dDrawn, setIs3dDrawn] = useState(false);
  const mark3dDrawn = useCallback(() => setIs3dDrawn(true), []);

  useEffect(() => {
    const layer = layerRef.current;
    const target = frameRef.current;
    if (!layer || !target || typeof ResizeObserver === 'undefined') return undefined;
    const update = () => setLayout(measure(target, layer));
    const observer = new ResizeObserver(update);
    observer.observe(layer);
    observer.observe(target);
    update();
    return () => observer.disconnect();
  }, [frameRef]);

  useEffect(() => {
    const layer = layerRef.current;
    if (!layer || mode === 'still' || typeof IntersectionObserver === 'undefined') return undefined;
    const observer = new IntersectionObserver((entries) => {
      if (!entries.some((entry) => entry.isIntersecting)) return;
      setIsVisible(true);
      observer.disconnect();
    });
    observer.observe(layer);
    return () => observer.disconnect();
  }, [mode]);

  return { layerRef, layout, motion: isVisible && mode !== 'still' ? mode : null, is3dDrawn, mark3dDrawn };
}
